import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';

import { type ApiError, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

export const SendToThirdPartyVariablesSchema = z.object({
  third_party_company_id: z.string(),
  additional_invoices: z.array(z.number()),
  // Bailiff
  contested: z.boolean().optional(),
  // Legalcity
  recovery_service_object: z.string().optional(),
  debtor_legalrep_first_name: z.string().optional(),
  debtor_legalrep_last_name: z.string().optional(),
  recovery_due_date: z.string().optional(),
  // Gcollect
  ape_code: z.string().optional(),
  siren: z.string().optional(),
});

const SendToThirdPartyResponseSchema = z.object({
  message: z.string(),
  third_party_case_ids: z.array(z.number()),
});

export type SendToThirdPartyVariables = z.infer<typeof SendToThirdPartyVariablesSchema>;
export type SendToThirdPartyResponse = z.infer<typeof SendToThirdPartyResponseSchema>;

export async function sendToThirdPartyFn(
  axiosInstance: Promise<AxiosInstance>,
  { id, data }: { id: string; data: SendToThirdPartyVariables },
) {
  const instance = await axiosInstance;
  const parsed = SendToThirdPartyVariablesSchema.parse(data);

  const res = await instance.post<SendToThirdPartyResponse>(
    `invoices/${id}/send_to_third_party`,
    parsed,
  );
  return SendToThirdPartyResponseSchema.parse(res.data);
}

export function useSendToThirdParty() {
  const axiosInstance = useLegacyAxiosInstance();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    SendToThirdPartyResponse,
    ApiError,
    { id: string; data: SendToThirdPartyVariables }
  >({
    mutationFn: variables => sendToThirdPartyFn(axiosInstance, variables),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['third-party-cases'] });
      queryClient.invalidateQueries({ queryKey: ['unhandledClient'] });
      queryClient.invalidateQueries({ queryKey: ['unhandledClients'] });
      queryClient.invalidateQueries({ queryKey: ['unhandled-debtors-view'] });
    },
  });

  return addActionNameToMutationResult<
    SendToThirdPartyResponse,
    ApiError,
    { id: string; data: SendToThirdPartyVariables },
    'sendToThirdParty'
  >('sendToThirdParty', mutation);
}
